"use client";

import { Suspense, type ReactNode } from "react";
import { usePathname } from "next/navigation";

import {
  SidebarInset,
  SidebarProvider,
} from "@gogive/design-system/components/sidebar";

import { BreadcrumbRegistryProvider } from "@/breadcrumb";
import { TopBarActionsRegistryProvider } from "@/top-bar-actions";
import { RouteDocumentTitle } from "@/components/DocumentTitle";

import SideNav from "./SideNav";
import { TopBar } from "./TopBar";
import { NotificationToasts } from "./InfoBar";
import { StripeConnectNudge } from "./stripe/StripeConnectNudge";
import { StripeReturnDialog } from "./stripe/StripeReturnDialog";
import {
  isFlatContentShellPath,
  isFullHeightFlatShellPath,
} from "@/lib/page-shell-layout";
import { cn } from "@/lib/utils";

/** Atlas-style shell: sidebar and content column share one edge; top bar spans content only. */
function isHomeDashboardPath(pathname: string) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === "/";
}

export function CharitySidebarShell({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? "/";
  const isHomeDashboard = isHomeDashboardPath(pathname);
  const isFlatContent = isFlatContentShellPath(pathname);
  const isFullHeightFlat = isFullHeightFlatShellPath(pathname);
  const useInsetCard = !isHomeDashboard && !isFlatContent;

  return (
    <SidebarProvider
      id="charity-sidebar-shell"
      className="flex h-svh min-h-0 w-full overflow-hidden bg-background"
    >
      <BreadcrumbRegistryProvider>
        <TopBarActionsRegistryProvider>
          <RouteDocumentTitle />
          <SideNav />
          <SidebarInset className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
            <TopBar />
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col overflow-hidden bg-background",
                isFlatContent && !isHomeDashboard ? "p-0" : "p-2 pt-3",
              )}
            >
              <NotificationToasts />
              <main
                className={cn(
                  "relative flex min-h-0 flex-1 flex-col overscroll-contain",
                  useInsetCard
                    ? "overflow-y-auto rounded-lg border border-border bg-card shadow-xs"
                    : isFullHeightFlat
                      ? "h-0 overflow-hidden bg-background"
                      : "overflow-y-auto bg-background",
                )}
              >
                <Suspense fallback={null}>
                  <StripeReturnDialog />
                  <StripeConnectNudge />
                </Suspense>
                {isFullHeightFlat ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    {children}
                  </div>
                ) : (
                  children
                )}
              </main>
            </div>
          </SidebarInset>
        </TopBarActionsRegistryProvider>
      </BreadcrumbRegistryProvider>
    </SidebarProvider>
  );
}
